// SEQ-193 follow-on: the Commercial Dashboard's "P&L month on month" section.
//
// Pure logic only (no React, no CSS), so a deno test can import it. The section
// renders the SAME PnlMonthOnMonthTable the P&L Analytics widget renders, from
// a PnlWidgetConfig this file builds, fed through the SAME request planner
// (buildPnlRequestPlan) and data hook (usePnlDashboardData) the P&L Analytics
// page uses. Nothing here recalculates a figure.
//
// DEFAULTS, AND WHERE THEY COME FROM
// - Metric: net profit. P&L Analytics has no seeded month on month widget; a
//   new widget starts on `calculated.net_profit` (newWidgetDraft in
//   widgetEditorState.ts) and switching it to month on month keeps that single
//   metric (monthOnMonthChartTypePatch). Same metric here.
// - Months: a month on month widget has no month count of its own, it shows
//   every month of its date range, and a new widget's range is financial year
//   to date (newWidgetDraft). So with no Dashboard date filter this shows the
//   financial year to date, complete months only. If that leaves no complete
//   month (early July) it falls back to the last FALLBACK_MONTHS complete months.
// - Company total (all projects), change column on (a widget's own default is
//   off; the Dashboard brief asks for it on).
//
// FILTER MAPPING (Dashboard filter state -> P&L query)
// - Projects, builders and project status are already folded together by the
//   Dashboard filter context into `effectiveProjectIds` (null means "all").
//   That list is sent as the P&L `projectIds`. An EMPTY non-null list means the
//   filters match no project, and the P&L RPC would read an empty list as
//   "all projects", so it is never sent: the section says nothing matches.
// - Date range: the Dashboard's month range, clamped to the last COMPLETE
//   month. "All time" cannot be laid out month by month, so it takes the
//   default above.
// - Variation status filter: does not apply to P&L data, ignored.
//
// Pure string arithmetic on ISO dates, like monthOnMonth.ts, so the result is
// the same in every timezone.

import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema.ts";
import { datePreset } from "../../lib/pnl/fiscalYear.ts";
import type { DateRange as DashboardMonthRange, DatePreset } from "../../types/dashboard-filters.ts";

export const DASHBOARD_MOM_WIDGET_ID = "dashboard-pnl-month-on-month";
export const DASHBOARD_MOM_METRIC = "calculated.net_profit";
/** Used only when financial year to date has no complete month yet. */
export const DASHBOARD_MOM_FALLBACK_MONTHS = 6;

/** The widget config the Dashboard section renders: the same shape the P&L Analytics editor writes for a month on month widget. */
export function buildDashboardMonthOnMonthConfig(): PnlWidgetConfig {
  return {
    schemaVersion: 1,
    widgetId: DASHBOARD_MOM_WIDGET_ID,
    name: "P&L month on month",
    chartType: "month_on_month",
    metric: DASHBOARD_MOM_METRIC,
    metrics: [DASHBOARD_MOM_METRIC],
    groupBy: "company",
    dataGrain: "month",
    aggregation: "sum",
    filters: [],
    dateRange: { preset: "financial_year_to_date" },
    dateRangeMode: "dashboard",
    displayAs: "currency",
    showChange: true,
    layout: { w: 4, h: 2 },
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function monthStartIso(year: number, month: number): string {
  return `${String(year).padStart(4, "0")}-${pad2(month)}-01`;
}

function monthEndIso(year: number, month: number): string {
  return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(daysInMonth(year, month))}`;
}

function parseToday(todayIso: string): { year: number; month: number } {
  return { year: Number(todayIso.slice(0, 4)), month: Number(todayIso.slice(5, 7)) };
}

/** The last day of the month before today's month: the latest date a complete month can end on. */
export function lastCompleteMonthEnd(todayIso: string): string {
  const { year, month } = parseToday(todayIso);
  return month === 1 ? monthEndIso(year - 1, 12) : monthEndIso(year, month - 1);
}

/** First day of the month `count - 1` months before the month of `endIso`, so `count` months end at `endIso`'s month. */
function monthStartCountBack(endIso: string, count: number): string {
  let year = Number(endIso.slice(0, 4));
  let month = Number(endIso.slice(5, 7)) - (count - 1);
  while (month < 1) {
    month += 12;
    year -= 1;
  }
  return monthStartIso(year, month);
}

export interface DashboardMomFilterInput {
  datePreset: DatePreset;
  dateRange: DashboardMonthRange;
}

/**
 * The ISO range the section asks for, or null when the Dashboard's own range
 * holds no complete month (this month only, or a range that starts after the
 * last complete month).
 */
export function resolveDashboardMomRange(
  filters: DashboardMomFilterInput,
  todayIso: string,
): { from: string; to: string } | null {
  const latestEnd = lastCompleteMonthEnd(todayIso);

  if (filters.datePreset === "all_time") {
    const fytd = datePreset("financial_year_to_date", todayIso);
    if (fytd.from !== null && fytd.from <= latestEnd) return { from: fytd.from, to: latestEnd };
    return { from: monthStartCountBack(latestEnd, DASHBOARD_MOM_FALLBACK_MONTHS), to: latestEnd };
  }

  const { startYear, startMonth, endYear, endMonth } = filters.dateRange;
  const from = monthStartIso(startYear, startMonth);
  const requestedEnd = monthEndIso(endYear, endMonth);
  const to = requestedEnd < latestEnd ? requestedEnd : latestEnd;
  if (from > to) return null;
  return { from, to };
}

export type DashboardMomProjects =
  | { kind: "all" }
  | { kind: "some"; projectIds: string[] }
  | { kind: "none" };

/** Maps the Dashboard's effective project list onto the P&L `projectIds` filter, refusing the empty list that the RPC would read as "all". */
export function mapDashboardProjects(effectiveProjectIds: string[] | null): DashboardMomProjects {
  if (effectiveProjectIds === null) return { kind: "all" };
  if (effectiveProjectIds.length === 0) return { kind: "none" };
  return { kind: "some", projectIds: effectiveProjectIds };
}

/** Whether the viewer may see this section at all. Same flag that gates the P&L Analytics route and sidebar link. */
export function canSeeDashboardPnlMonthOnMonth(profile: { can_view_financials?: boolean | null } | null | undefined): boolean {
  return Boolean(profile?.can_view_financials);
}
